import {
  AgentActionSchema,
  ActionSchema,
  ForeachActionSchema,
  IfActionSchema,
  SwitchActionSchema,
  TriggerSchema,
  UntilActionSchema,
  isAgentAction,
  type Action,
  type Trigger,
  type WorkflowDefinition,
} from '@/types/workflowDefinition';
import type { NodeDefinitionTarget } from '@/types/nodeCodeEditing';
import type { SchemaObject } from '@/types/valueSegment';
import { getBuiltInManifest } from '@/data/manifests';
import { findActionLocations } from './walkActions';

export type NodeCodeDefinition = Action | Trigger;

export type NodeCodeValidationResult =
  | { success: true; value: NodeCodeDefinition }
  | { success: false; error: string };

export interface NodeDefinitionLocation {
  definition: NodeCodeDefinition;
  container: Record<string, NodeCodeDefinition>;
  key: string;
}

const formatSchemaError = (issues: Array<{ path: PropertyKey[]; message: string }>): string => {
  const issue = issues[0];
  if (!issue) return 'The JSON does not match the node schema.';
  const path = issue.path.length > 0 ? `${issue.path.join('.')}: ` : '';
  return `${path}${issue.message}`;
};

const getPathValue = (
  root: Record<string, unknown>,
  path: string[],
): { found: boolean; value: unknown } => {
  let current: unknown = root;
  for (const segment of path) {
    if (
      !current ||
      typeof current !== 'object' ||
      Array.isArray(current) ||
      !Object.hasOwn(current, segment)
    ) {
      return { found: false, value: undefined };
    }
    current = (current as Record<string, unknown>)[segment];
  }
  return { found: true, value: current };
};

const validateRequiredSchemaProperties = (
  value: unknown,
  schema: SchemaObject,
  path: string[],
): string | null => {
  if (!Array.isArray(schema.required) || schema.required.length === 0) {
    return null;
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return `${path.join('.') || 'inputs'}: Must be an object with required properties`;
  }
  for (const property of schema.required) {
    if (!Object.hasOwn(value, property)) {
      return `${[...path, property].join('.')}: Required`;
    }
  }
  return null;
};

const validateOperationStructure = (
  parsed: Record<string, unknown>,
  expectedType: string,
  currentDefinition: NodeCodeDefinition | undefined,
): string | null => {
  const specificSchema =
    expectedType === 'If'
      ? IfActionSchema
      : expectedType === 'Switch'
        ? SwitchActionSchema
        : expectedType === 'Foreach'
          ? ForeachActionSchema
          : expectedType === 'Until'
            ? UntilActionSchema
            : expectedType === 'Agent'
              ? AgentActionSchema
              : null;
  if (specificSchema) {
    const result = specificSchema.safeParse(parsed);
    if (!result.success) {
      return formatSchemaError(result.error.issues);
    }
  }

  const kind = typeof parsed.kind === 'string' ? parsed.kind : undefined;
  const manifest = getBuiltInManifest(expectedType, kind);
  const inputSchema = manifest?.properties.inputs;
  if (inputSchema) {
    const inputPath = manifest.properties.inputsLocation ?? ['inputs'];
    const resolved = getPathValue(parsed, inputPath);
    if (!resolved.found && manifest.properties.isInputsOptional !== true) {
      return `${inputPath.join('.') || 'inputs'}: Required`;
    }
    if (resolved.found) {
      return validateRequiredSchemaProperties(
        resolved.value,
        inputSchema,
        inputPath,
      );
    }
  }

  // Dynamic connector operations do not have a static built-in manifest. Their
  // existing definition still tells us whether `inputs` is part of the runtime
  // contract; prevent accidental key renames while preserving unknown fields.
  if (
    !manifest &&
    currentDefinition &&
    Object.hasOwn(currentDefinition, 'inputs') &&
    !Object.hasOwn(parsed, 'inputs')
  ) {
    return 'inputs: Required';
  }

  return null;
};

export const validateNodeCode = (
  text: string,
  target: NodeDefinitionTarget,
  expectedType: string | undefined,
  currentDefinition?: NodeCodeDefinition,
): NodeCodeValidationResult => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Invalid JSON.',
    };
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { success: false, error: 'Node JSON must be an object.' };
  }

  const parsedType = (parsed as Record<string, unknown>).type;
  if (typeof parsedType !== 'string' || parsedType.length === 0) {
    return { success: false, error: 'type: Required' };
  }
  if (expectedType && parsedType !== expectedType) {
    return {
      success: false,
      error: `Changing the operation type is not supported here. Keep type as "${expectedType}".`,
    };
  }

  const result =
    target.kind === 'trigger'
      ? TriggerSchema.safeParse(parsed)
      : ActionSchema.safeParse(parsed);
  if (!result.success) {
    return { success: false, error: formatSchemaError(result.error.issues) };
  }

  const structureError = validateOperationStructure(
    parsed as Record<string, unknown>,
    parsedType,
    currentDefinition,
  );
  if (structureError) {
    return { success: false, error: structureError };
  }

  // Zod is a guard only. Commit the original object so passthrough fields are
  // never lost through schema transformations.
  return { success: true, value: parsed as NodeCodeDefinition };
};

const resolveActionLocation = (
  actions: Record<string, Action>,
  name: string,
  scopedName: string,
) => {
  const locations = findActionLocations(actions, name, 'definition.actions');
  const exact = locations.filter((location) => location.scopedName === scopedName);
  if (exact.length === 1) return exact[0]!;
  return null;
};

export const resolveNodeDefinitionLocation = (
  workflow: WorkflowDefinition,
  target: NodeDefinitionTarget,
): NodeDefinitionLocation | null => {
  if (target.kind === 'trigger') {
    const definition = workflow.definition.triggers[target.name];
    if (!definition) return null;
    return {
      definition,
      container: workflow.definition.triggers as Record<string, NodeCodeDefinition>,
      key: target.name,
    };
  }

  if (target.kind === 'action') {
    const location = resolveActionLocation(
      workflow.definition.actions,
      target.name,
      target.scopedName,
    );
    if (!location) return null;
    return {
      definition: location.action,
      container: location.container as Record<string, NodeCodeDefinition>,
      key: location.name,
    };
  }

  const agentName = target.agentScopedName.split('/').pop();
  if (!agentName) return null;
  const agentLocation = resolveActionLocation(
    workflow.definition.actions,
    agentName,
    target.agentScopedName,
  );
  if (!agentLocation || !isAgentAction(agentLocation.action)) return null;

  const tool = agentLocation.action.tools?.[target.toolName];
  const definition = tool?.actions?.[target.innerActionName];
  if (!tool?.actions || !definition) return null;
  return {
    definition,
    container: tool.actions as Record<string, NodeCodeDefinition>,
    key: target.innerActionName,
  };
};

export const serializeNodeDefinition = (definition: NodeCodeDefinition): string =>
  JSON.stringify(definition, null, 2);
